import React, { useState, useEffect } from "react"
// 1. Import useNavigate
import { useNavigate } from "react-router-dom"
import { AlertTriangle, Clock, Receipt, DollarSign, Link } from "lucide-react"
import { getClientInvoices, getClientPayments, getClientCredits } from "../../api/apiClient"

const UserInvoices = () => {
  const [invoices, setInvoices] = useState([])
  const [payments, setPayments] = useState([])
  const [credits, setCredits] = useState({ issued: [], applied: [], availableCredit: 0 })
  const [summary, setSummary] = useState({ totalDue: 0, nextDueDate: "N/A" })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // 2. Initialize the navigate function
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const [invoiceData, paymentData, creditData] = await Promise.all([
          getClientInvoices(),
          getClientPayments(),
          getClientCredits(),
        ])

        // --- Process Invoices ---
        setInvoices(invoiceData)
        const balance = invoiceData.reduce(
          (acc, inv) => acc + inv.balanceDue,
          0
        )
        const unpaidInvoices = invoiceData
          .filter(inv => inv.balanceDue > 0)
          .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))

        setSummary({
          totalDue: balance,
          nextDueDate:
            unpaidInvoices.length > 0
              ? new Date(unpaidInvoices[0].dueDate).toLocaleDateString()
              : "N/A",
        })

        // --- Process Payments ---
        setPayments(paymentData)
        
        // --- Process Credits ---
        setCredits(creditData)
      } catch (err) {
        setError(err.message || "Failed to load financial data.")
        setInvoices([])
        setPayments([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const renderStatusBadge = balanceDue => {
    // ... (no changes)
    const status = balanceDue > 0 ? "Unpaid" : "Paid"
    const statusClasses = {
      Paid: "bg-green-100 text-green-800",
      Unpaid: "bg-red-100 text-red-800",
    }
    return (
      <span
        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
          statusClasses[status] || "bg-yellow-100 text-yellow-800"
        }`}
      >
        {status}
      </span>
    )
  }

  // --- Rendering States ---
  if (isLoading) {
    // ... (no changes)
    return (
      <div className="flex items-center justify-center p-12 bg-white rounded-xl shadow-lg">
        <Clock className="w-6 h-6 animate-spin text-indigo-600 mr-3" />
        <p className="text-gray-600">Loading financial data...</p>
      </div>
    )
  }

  if (error) {
    // ... (no changes)
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg shadow-lg">
        <AlertTriangle className="w-5 h-5 text-red-600 inline mr-2" />
        <p className="text-red-700 font-medium">Error: {error}</p>
      </div>
    )
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg">
      <h3 className="text-xl font-semibold mb-4 text-gray-700 flex items-center">
        <Receipt className="w-6 h-6 mr-2 text-indigo-600" />
        Invoices & Balance
      </h3>

      {/* BALANCE CARD */}
      <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 border border-indigo-200 bg-indigo-50 rounded-lg">
          <p className="text-sm text-indigo-700 font-medium">
            Remaining Balance:
          </p>
          <p className="text-3xl font-bold text-indigo-600">
            {summary.totalDue.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </p>
        </div>
        <div className="p-4 border border-teal-200 bg-teal-50 rounded-lg">
          <p className="text-sm text-teal-700 font-medium">
            Available Credit:
          </p>
          <p className="text-3xl font-bold text-teal-600">
            {credits.availableCredit.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </p>
        </div>
      </div>

      {/* --- INVOICE TABLE --- */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Invoice ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date Issued
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Due Date
              </th>
              <th className="px-6 py-3 text-right text-xs font-MEdium text-gray-500 uppercase tracking-wider">
                Original Amount
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Balance Due
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invoices.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  No invoice history found.
                </td>
              </tr>
            ) : (
              invoices.map(invoice => (
                <tr key={invoice.invoiceID}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {invoice.invoiceID}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(invoice.invoiceDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(invoice.dueDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                    {invoice.totalAmount.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-700 text-right">
                    {invoice.balanceDue.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {renderStatusBadge(invoice.balanceDue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {/* 3. Updated Button */}
                    <button
                      onClick={() => navigate(`/bookings/${invoice.bookingID}`)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      View Booking Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* --- PAYMENT HISTORY TABLE --- */}
      <div className="mt-8">
        <h3 className="text-xl font-semibold mb-4 text-gray-700 flex items-center">
          <DollarSign className="w-6 h-6 mr-2 text-green-600" />
          Payment History
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Processed By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Link
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No payment history found.
                  </td>
                </tr>
              ) : (
                payments.map(payment => (
                  <tr key={payment.paymentID}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(payment.paymentDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {payment.paymentAmount.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payment.paymentMethods}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payment.processedBy || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payment.cloverLink ? (
                        <a
                          href={payment.cloverLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-900 flex items-center"
                        >
                          <Link className="w-4 h-4 mr-1" />
                          View Receipt
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- CREDIT HISTORY SECTION --- */}
      <div className="mt-8">
        <h3 className="text-xl font-semibold mb-4 text-gray-700 flex items-center">
          <DollarSign className="w-6 h-6 mr-2 text-teal-600" />
          Credit History
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Credits Issued */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-teal-100 border-b border-teal-200">
              <h4 className="font-semibold text-teal-800">Credits Issued (Added)</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-teal-200">
                <thead className="bg-teal-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-teal-700 uppercase">Date</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-teal-700 uppercase">Amount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-teal-700 uppercase">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-teal-200 bg-white">
                  {credits.issued.length === 0 ? (
                    <tr><td colSpan="3" className="px-4 py-3 text-center text-sm text-gray-500">No credits issued.</td></tr>
                  ) : (
                    credits.issued.map(c => (
                      <tr key={c.creditID}>
                        <td className="px-4 py-2 text-sm text-gray-700">{new Date(c.creditDate).toLocaleDateString()}</td>
                        <td className="px-4 py-2 text-sm font-medium text-teal-700 text-right">{c.amount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{c.reason}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Credits Applied */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-100 border-b border-gray-200">
              <h4 className="font-semibold text-gray-800">Credits Applied (Used)</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Date Used</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Amount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Invoice #</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {credits.applied.length === 0 ? (
                    <tr><td colSpan="3" className="px-4 py-3 text-center text-sm text-gray-500">No credits used.</td></tr>
                  ) : (
                    credits.applied.map(c => (
                      <tr key={`${c.invoiceID}-${c.applicationDate}`}>
                        <td className="px-4 py-2 text-sm text-gray-700">{new Date(c.applicationDate).toLocaleDateString()}</td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-800 text-right">{c.amountApplied.toLocaleString("en-US", { style: "currency", currency: "USD" })}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{c.invoiceID}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserInvoices
